import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronRight, Loader2, Star } from "lucide-react";
import { ProfileMenu } from "@/components/ProfileMenu";
import { useAuth } from "@/lib/auth";
import { getFavoriteRecipes, removeFavoriteRecipe } from "@/lib/mealPlan";
import { RecipeDialog } from "./RecipeDialog";

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const data = await getFavoriteRecipes();
      setFavorites(data || []);
    } catch (error) {
      console.error("Error loading favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (recipeId: string) => {
    try {
      await removeFavoriteRecipe(recipeId);
      setFavorites(favorites.filter((fav) => fav.id !== recipeId));
    } catch (error) {
      console.error("Error removing favorite:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <div className="w-full bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="/PlateMate_logo.svg"
              alt="PlateMate"
              className="h-10 w-auto"
            />
            <h1 className="text-xl font-semibold">המתכונים המועדפים שלי</h1>
          </div>
          {user && <ProfileMenu user={user} />}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="rtl-button-icon"
          >
            חזור לתוכניות
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-12">
            <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              עדיין אין לך מתכונים מועדפים
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {favorites.map((favorite) => (
              <Card
                key={favorite.id}
                className="p-6 hover:shadow-md transition-shadow"
              >
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 -mt-2 -mr-2"
                      onClick={() => handleRemoveFavorite(favorite.id)}
                    >
                      הסר ממועדפים
                    </Button>
                    <h3 className="text-xl font-semibold text-right">
                      {favorite.recipe.name}
                    </h3>
                  </div>

                  <p className="text-muted-foreground text-right">
                    {favorite.recipe.description}
                  </p>

                  <div className="flex justify-end">
                    <RecipeDialog
                      meal={favorite.recipe}
                      isFavorite={true}
                      onToggleFavorite={() => handleRemoveFavorite(favorite.id)}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
